import React, {useEffect, useMemo, useState} from "react";
import {Controller, useFormContext} from "react-hook-form";
import useTranslate from "../../../../core/languages/hooks/useTranslate";
import InputField from "../../../../ui/components/InputField/InputField";
import Label from "../../../../ui/components/Label/Label";
import Select from "../../../../ui/shared/Select/Select";
import {DIAL_CODES} from "../../../../ui/data/dialCodes";
import Input from "../../../../ui/shared/Input/Input";
import InputError from "../../../../ui/shared/InputError/InputError";
import BookingNationalInputs from "../../../../booking/components/BookingNationalInputs";
import moment from "moment";
import {parseInt} from "lodash";
import Profile from "../../../../profile/types/Profile";
import PaymentCardSelector from "../../../../payment/components/PaymentCardSelector";
import {useOidcClientContext} from "../../../../ui/context/OidcClientContext";
import HotelDetail from "../../../../hotels/types/HotelDetail";
import Prebooking from "../../../../booking/types/Prebooking";
import {WizardSaveInput} from "../../../types/WizardSaveInput";
import useCustomerContext from "../../../../customers/hooks/useCustomerContext";
import LegalCheckModal from "../../../../common/components/LegalCheckModal";

interface StepPersonalDetailsProps {
  hotelInfo: HotelDetail;
  profile?: Profile;
  preBooking: Prebooking;
}
const StepPersonalDetails: React.FC<StepPersonalDetailsProps> = ({hotelInfo, profile, preBooking}) => {

  const {control, register, watch, setValue, formState: {errors}} = useFormContext<WizardSaveInput>();
  const t = useTranslate();
  const [creditCardExpirationError, setCreditCardExpirationError] = React.useState<string | null>(null);
  const [paymentCardError, setPaymentCardError] = useState<string | null>(null);
  const {authenticated} = useOidcClientContext();
  const { customerTranslation } = useCustomerContext();

  const isDirectPayment = useMemo(() => {
    return hotelInfo.paymentMethod === 'direct_payment' || false;
  }, [hotelInfo]);

  const isPaymentRequired = useMemo(() => {
    return hotelInfo.paymentMethod === 'payment_request' || false;
  }, [hotelInfo]);

  useEffect(() => {
    if (!profile) return;

    setValue("name", preBooking.name || "");
    setValue("surname", preBooking.surname || "");
    setValue("email", preBooking.email || "");
    setValue("telephone", preBooking.phoneNumber || "");
    setValue("invoiceNameAndSurname", profile.invoiceFullName || "");
    setValue("invoiceAddress", profile.invoiceAddress || "");

    if (preBooking.iso2 && preBooking.dialCode) {
      setValue("dial", `${preBooking.iso2}-${preBooking.dialCode}`);
    }

    setValue("cardName", profile.cardName || "");
    setValue("cardNumber", profile.cardNumberDecrypted || "");
    if (profile.cardExpiration) {
      const [month, year] = profile.cardExpiration.split('/');
      setValue("cardDateMonth", month);
      setValue("cardDateYear", year);
    }
  }, [profile, setValue]);

  const [creditCardExpirationMonth, creditCardExpirationYear] = watch(['cardDateMonth', 'cardDateYear']);

  useEffect(() => {
    if (!creditCardExpirationMonth || !creditCardExpirationYear) return;

    // Validate credit card expiration against check-in date
    const bookFrom = moment().format('DD/MM/YYYY'); // Using current date
    const expirationDate = moment({
      year: parseInt(`20${creditCardExpirationYear}`),
      month: parseInt(creditCardExpirationMonth) - 1,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0
    });

    if (moment(`${bookFrom} 00:00:00`, 'DD/MM/YYYY HH:mm:ss').isAfter(expirationDate)) {
      setCreditCardExpirationError(t('booking.invalidCreditCardExpirationActualDate'));
    } else {
      setCreditCardExpirationError(null);
    }
  }, [creditCardExpirationMonth, creditCardExpirationYear, t]);

  return (
    <div className="space-y-6">

      {/* PERSONAL INFORMATION */}
      <div>
        <h3 className="text-2xl font-semibold mb-5">{t("booking.personalInformation")}</h3>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 my-5"></div>

        {/* NAME, SURNAME, EMAIL */}
        <div className="lg:flex lg:space-x-5 mb-6">
          <div className="lg:w-1/2 mb-2">
            <Controller
              name="name"
              control={control}
              rules={{
                required: t("common.feedback.field_required")
              }}
              render={({field}) => (
                <InputField
                  {...field}
                  label={t("booking.name")}
                  type="text"
                  readOnly={preBooking.name}
                  error={errors.name?.message}
                />
              )}
            />
          </div>

          <div className="lg:w-1/2 mb-2">
            <Controller
              name="surname"
              control={control}
              rules={{
                required: t("common.feedback.field_required")
              }}
              render={({field}) => (
                <InputField
                  {...field}
                  label={t("booking.surname")}
                  type="text"
                  readOnly={preBooking.surname}
                  error={errors.surname?.message}
                />
              )}
            />
          </div>
        </div>

        <div className="lg:flex lg:space-x-5 mb-6">
          <div className="lg:w-1/2 mb-2">
            <Controller
              name="email"
              control={control}
              rules={{
                required: t("common.feedback.field_required")
              }}
              render={({field}) => (
                <InputField
                  {...field}
                  label={t("booking.email")}
                  type="email"
                  readOnly={preBooking.email}
                  error={errors.email?.message}
                />
              )}
            />
          </div>

          {/* TELEPHONE */}
          <div className="lg:w-1/2 space-y-1 mb-2">
            <Label isInvalid={!!errors.dial || !!errors.telephone}>
              {t("booking.telephone")}
            </Label>

            <div className="flex">
              {/* DIAL */}
              <Controller
                name="dial"
                control={control}
                rules={{
                  required: t("common.feedback.field_required")
                }}
                render={({field}) => (
                  <Select
                    {...field}
                    className="w-1/2 rounded-tr-none rounded-br-none"
                    defaultValue="ES-34"
                    readOnly={preBooking.dialCode && preBooking.iso2}
                  >
                    {DIAL_CODES.map(d => (
                      <option
                        key={`${d.code}-${d.callingCode}`}
                        value={`${d.code}-${d.callingCode}`}
                      >
                        {d.code} +{d.callingCode}
                      </option>
                    ))}
                  </Select>
                )}
              />

              {/* TELEPHONE */}
              <Controller
                name="telephone"
                control={control}
                rules={{
                  required: t("common.feedback.field_required"),
                  pattern: {
                    value: /^[0-9]{6,13}$/,
                    message: t("booking.invalidTelephone")
                  }
                }}
                render={({field}) => (
                  <Input
                    {...field}
                    className="rounded-tl-none rounded-bl-none"
                    type="text"
                    readOnly={preBooking.phoneNumber}
                  />
                )}
              />
            </div>

            {(errors.dial || errors.telephone) && (
              <InputError>
                {errors.dial?.message || errors.telephone?.message}
              </InputError>
            )}
          </div>
        </div>

        {/* NATIONAL INPUTS */}
         <BookingNationalInputs
           t={t}
           errors={errors}
           register={register}
           watch={watch}
           setValue={setValue}
           profile={profile}
           control={control}
         />
      </div>

      {/* INVOICE AND CREDIT CARD INFORMATION */}
      <div>
        <h3 className="text-2xl font-semibold mb-5">{t("booking.creditCardInformation")}</h3>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 my-5"></div>

        {/* INVOICE INFORMATION */}
        <div className="lg:flex lg:space-x-5 mb-6">
          <div className="lg:w-1/2 mb-2">
            <Controller
              name="invoiceNameAndSurname"
              control={control}
              rules={{
                required: t("common.feedback.field_required")
              }}
              render={({field}) => (
                <InputField
                  {...field}
                  label={t("booking.invoiceNameAndSurname")}
                  error={errors.invoiceNameAndSurname?.message}
                />
              )}
            />
          </div>

          <div className="lg:w-1/2 mb-2">
            <Controller
              name="invoiceAddress"
              control={control}
              rules={{
                required: t("common.feedback.field_required")
              }}
              render={({field}) => (
                <InputField
                  {...field}
                  label={t("booking.invoiceAddress")}
                  error={errors.invoiceAddress?.message}
                />
              )}
            />
          </div>
        </div>

        {/* CREDIT CARD INFORMATION */}
        {(isDirectPayment || isPaymentRequired) && (
          <div className="space-y-2">
            <Controller
              name="selectedPaymentCardUuid"
              control={control}
              rules={{
                required: t("common.feedback.field_required")
              }}
              render={({field}) => (
                <PaymentCardSelector
                  value={field.value || null}
                  onChange={(uuid) => {
                    field.onChange(uuid);
                    if (uuid) setPaymentCardError(null);
                  }}
                  authenticated={authenticated}
                />
              )}
            />
            {creditCardExpirationError && (
              <p className="text-sm text-red-500 dark:text-red-400">{creditCardExpirationError}</p>
            )}
            {paymentCardError && (
              <p className="text-sm text-red-500 dark:text-red-400">{paymentCardError}</p>
            )}
          </div>
        )}
      </div>

      {/* LEGAL CHECKS */}
      <div>
        {customerTranslation?.privacyPolicy && (
          <LegalCheckModal
            name="privacyPolicy"
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            contentKey={customerTranslation?.privacyPolicy}
            legalLinkKey="footer.privacyPolicy"
          />
        )}
        {customerTranslation?.contractTerms && (
          <LegalCheckModal
            name="contractTerms"
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            contentKey={customerTranslation?.contractTerms}
            legalLinkKey="footer.contractTerms"
          />
        )}
      </div>
    </div>
  );
};

export default StepPersonalDetails;